import { useMemo, useState } from "react";
import { addDays, format, parseISO } from "date-fns";
import { CalendarDays, Check, Clock, LockKeyhole, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScheduleDateNavigation } from "@/components/schedule/ScheduleDateNavigation";
import { ScheduleSessionSurface } from "@/components/schedule/ScheduleSessionSurface";
import type { PublicTrialSlot } from "@/services/edgeFunctionWrappers/publicTrialsWrapper";
import { cn } from "@/lib/utils";

interface Props {
  slots: PublicTrialSlot[];
  value: PublicTrialSlot | null;
  onChange: (slot: PublicTrialSlot) => void;
  language: "fr" | "en" | "ar";
  t: (key: string, vars?: Record<string, string | number>) => string;
}

/** Pure public slot selector; token resolution and booking stay in the existing service. */
export function PublicTrialSlotPicker({ slots, value, onChange, language, t }: Props) {
  const [offset, setOffset] = useState(0);
  const sorted = useMemo(() => [...slots].sort((a, b) => a.class_date.localeCompare(b.class_date) || a.time.localeCompare(b.time)), [slots]);
  const first = sorted[0]?.class_date;
  const last = sorted[sorted.length - 1]?.class_date;
  if (!first || !last) return <p className="py-6 text-center text-sm text-muted-foreground">{t("slots.empty")}</p>;
  const start = addDays(parseISO(first), offset);
  const days = Array.from({ length: 3 }, (_, index) => addDays(start, index));
  const locale = language === "ar" ? "ar-SA" : language === "fr" ? "fr-FR" : "en-GB";
  const dateLabel = (date: Date, options: Intl.DateTimeFormatOptions) => date.toLocaleDateString(locale, options);

  return (
    <div className="space-y-3">
      <ScheduleDateNavigation
        label={`${dateLabel(start, { day: "numeric", month: "short" })} – ${dateLabel(days[2], { day: "numeric", month: "short", year: "numeric" })}`}
        canGoBack={offset > 0}
        canGoNext={format(addDays(start, 3), "yyyy-MM-dd") <= last}
        previousLabel={t("slots.previousDays")}
        nextLabel={t("slots.nextDays")}
        onPrevious={() => setOffset((current) => Math.max(0, current - 3))}
        onNext={() => setOffset((current) => current + 3)}
      />
      <div className="grid grid-cols-3 items-start gap-2" aria-label={t("sections.slot")}>
        {days.map((date) => {
          const key = format(date, "yyyy-MM-dd");
          const daySlots = sorted.filter((slot) => slot.class_date === key);
          return (
            <section key={key} className="min-w-0" data-trial-day={key}>
              <h4 className="mb-2 rounded-md border border-primary/25 bg-primary/5 py-2 text-center">
                <span className="block text-xs capitalize text-muted-foreground">{dateLabel(date, { weekday: "short" })}</span>
                <span className="block text-lg font-bold text-foreground">{dateLabel(date, { day: "numeric" })}</span>
              </h4>
              <div className="space-y-2">
                {daySlots.length === 0 && <p className="px-1 py-5 text-center text-xs text-muted-foreground">{t("slots.noDaySlots")}</p>}
                {daySlots.map((slot) => {
                  const full = slot.available <= 0;
                  const selected = value?.class_id === slot.class_id && value.class_date === slot.class_date;
                  return (
                    <ScheduleSessionSurface key={`${slot.class_id}-${key}`}>
                      <Button type="button" variant="outline" disabled={full} aria-pressed={selected}
                        aria-label={`${dateLabel(date, { day: "numeric", month: "long" })}, ${slot.time.slice(0, 5)}, ${slot.name}${full ? `, ${t("slots.full")}` : ""}`}
                        data-capacity-tone={selected ? "booked" : full ? "full" : slot.available === 1 ? "nearly-full" : "available"}
                        onClick={() => onChange(slot)}
                        className={cn("h-auto min-h-28 w-full min-w-0 flex-col items-stretch justify-start gap-1.5 whitespace-normal px-2 py-2.5 text-start hover:bg-card hover:text-card-foreground", selected && "ring-2 ring-primary ring-offset-2 ring-offset-background")}>
                        <span className="flex items-center justify-between gap-1 text-sm font-bold"><span className="inline-flex items-center gap-1"><Clock className="hidden h-3 w-3 sm:block" />{slot.time.slice(0, 5)}</span>{selected ? <Check className="h-4 w-4 shrink-0 text-primary" /> : full ? <LockKeyhole className="h-3 w-3 shrink-0" /> : null}</span>
                        <span className="break-words text-xs font-semibold leading-snug">{slot.name}</span>
                        {slot.instructor && <span className="break-words text-xs font-normal text-muted-foreground"><User className="me-1 inline h-3 w-3" />{slot.instructor}</span>}
                        <span className="mt-auto text-xs font-normal">{full ? t("slots.full") : t(slot.available === 1 ? "slots.spotsLeft" : "slots.spotsLeft_other", { count: slot.available })}</span>
                      </Button>
                    </ScheduleSessionSurface>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
      {value && <div role="status" className="flex items-start gap-2 rounded-md border border-primary/40 bg-primary/10 p-3 text-sm"><CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><div><p className="font-semibold text-primary">{t("slots.selected")}</p><p className="text-foreground">{dateLabel(parseISO(value.class_date), { weekday: "long", day: "numeric", month: "long" })} · {value.time.slice(0, 5)} · {value.name}</p></div></div>}
    </div>
  );
}